<template>
  <TabbedBox>
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-1">
        <i class="far fa-box-usd"></i> Moving Average Calculator
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-1">
        <ContentTitle>Moving Average Calculator</ContentTitle>
        <ContentSubTitle>Manually calculate moving averages for stock items</ContentSubTitle>

        <FormGroup>
          <div class="row">
            <div class="col-12">
              <FormRow label="Stock Item">
                <FormInputStockCode :stockType="InventoryStockTypes.glass" v-model="stockId" />
              </FormRow>
              <div v-if="hasStockSelected">
                <FormRow label="Glass Size">
                  <FormSelect v-model="glassSizeId" :options="glassSizeLookup" />
                </FormRow>
                <FormRow label="Select Date">
                  <FormDate v-model="referenceDate" />
                </FormRow>
                <FormRow label="Moving Average">
                  <FormInputCurrency v-model="movingAverage" placeholder="Moving Average Unit Price" :note="`As at ${formatDate(referenceDate)}`" />
                </FormRow>
                <FormRow label="Sheet Count On Hand" v-if="false">
                  <FormInputText type="number" v-model="sheetsOnHand" placeholder="Sheet Count" :note="`As at ${formatDate(referenceDate)}. Excludes offcuts`" />
                </FormRow>
                <FormRow label="Total Units On Hand">
                  <FormStaticLabel>{{formatDecimal(unitsOnHand, 2, true)}} m&sup2;
                    <br />
                    <small>As At {{formatDate(referenceDate)}}</small>
                  </FormStaticLabel>
                </FormRow>
                <FormRow label="Total Value On Hand">
                  <FormStaticLabel>{{formatCurrency(movingAverage * unitsOnHand)}}<br />
                    <small>As At {{formatDate(referenceDate)}}</small>
                  </FormStaticLabel>
                </FormRow>
                <FormHorizontalRule />
                <FormRow label="Received Items">
                  <StaticDataTable class="static-table-list">
                    <template v-slot:header>
                      <th>Quantity</th>
                      <th>Height</th>
                      <th>Width</th>
                      <th>Units</th>
                      <th>Unit Price</th>
                      <th>Price</th>
                      <th>Weighting</th>
                      <th>Additional Costs</th>
                      <th>Adj. Line Price</th>
                      <th>Actions</th>
                    </template>
                    <template v-slot:body>
                      <tr v-for="(recvItem, index) in recvItemList" :key="index">
                        <td>
                          <FormInputText type="number" v-model="recvItem.quantity" placeholder="Quantity" />
                        </td>
                        <td>
                          <FormInputText type="number" v-model="recvItem.height" placeholder="Height" addon="mm" />
                        </td>
                        <td>
                          <FormInputText type="number" v-model="recvItem.width" placeholder="Width" addon="mm" />
                        </td>
                        <td>
                          {{formatDecimal(calculateItemUnits(recvItem, 2, true))}} m&sup2;
                        </td>
                        <td>
                          <FormInputCurrency v-model="recvItem.unitPrice" placeholder="Unit Price" />
                        </td>
                        <td>
                          {{formatCurrency(calculatePrice(recvItem))}}
                        </td>
                        <td>
                          {{formatPercent(calculateWeighting(recvItem) * 100.0)}}
                        </td>
                        <td>
                          {{formatCurrency(calculateItemAddCosts(recvItem))}}
                        </td>
                        <td>
                          {{formatCurrency(calculateItemAdjLinePrice(recvItem))}}
                        </td>
                        <td>
                          <ActionDropMenu tableAction>
                            <DropdownMenuItem @click="removeRecvItem(index)" icon="fa-trash-alt">Remove</DropdownMenuItem>
                          </ActionDropMenu>
                        </td>
                      </tr>
                    </template>
                  </StaticDataTable>
                  <FormButton @click="addRecvItem" class="btn-default btn-xs"><i class="far fa-plus-circle"></i> Add Item</FormButton>
                </FormRow>
                <FormRow label="Additional Costs">
                  <StaticDataTable class="static-table-list">
                    <template v-slot:header>
                      <th width="50%">Description</th>
                      <th width="40%">Amount</th>
                      <th width="10%">Actions</th>
                    </template>
                    <template v-slot:body>
                      <tr v-for="(costItem, index) in additionalCostList" :key="index">
                        <td>
                          <FormInputText v-model="costItem.description" placeholder="Description" />
                        </td>
                        <td>
                          <FormInputCurrency v-model="costItem.amount" placeholder="Amount" />
                        </td>
                        <td>
                          <ActionDropMenu tableAction>
                            <DropdownMenuItem @click="removeAdditionalCost(index)" icon="fa-trash-alt">Remove</DropdownMenuItem>
                          </ActionDropMenu>
                        </td>
                      </tr>
                      <tr v-if="additionalCostList.length > 0">
                        <td class="font-weight-bold">
                          Total:
                        </td>
                        <td class="font-weight-bold">
                          {{formatCurrency(totalAdditionalCosts)}}
                        </td>
                        <td></td>
                      </tr>
                    </template>
                  </StaticDataTable>
                  <FormButton @click="addAdditionalCost" class="btn-default btn-xs"><i class="far fa-plus-circle"></i> Add Item</FormButton>
                </FormRow>
                <FormHorizontalRule />
                <FormRow label="Calculated Moving Average Unit Price">
                  <FormStaticLabel>
                    <strong>{{formatCurrency(newMovingAveragePrice)}}</strong>
                  </FormStaticLabel>
                </FormRow>
              </div>
            </div>
          </div>
        </FormGroup>

      </TabPage>
    </template>
  </TabbedBox>
</template>


<script>
import moment from 'moment'

import FormInputStockCode from '@/modules/inventory/components/formInputStockCode.vue'
import { InventoryStockTypes } from '@/store/types'

export default {
  name: 'AdminToolMovingAvgCalc',
  components: {
    FormInputStockCode
  },
  data() {
    return {
      stockId: null,
      stock: {},
      glassSizeId: null,
      glassSizeList: [],
      referenceDate: moment().format('YYYY-MM-DD'),
      sheetsOnHand: 0,
      unitsOnHand: 0,
      movingAverage: 0.0,
      recvItemList: [],
      additionalCostList: []
    }
  },
  watch: {
    stockId: function() {
      this.glassSizeId = null
      this.glassSizeList = []

      // Query the stock info     
      this.$apiService.inventoryService.getStock(this.stockId)
      .then(response => {
        this.stock = response.data
        // Glass sizes
        this.glassSizeList = response.data.glassSizeList.filter(glassSize => glassSize.isActive)

        // Default to first size
        if(this.glassSizeList.length > 0)
          this.glassSizeId = this.glassSizeList[0].id  
      })
      .catch(err => {
        this.stock = {}
        console.error(err)
      })   
    },
    glassSizeId: function() {
      if(!this.hasStockSelected || !this.hasGlassSize)
        return

      this.requestMovingAvgData()      
    },
    referenceDate: function() {
      if(!this.hasStockSelected || !this.hasGlassSize)
        return

      this.requestMovingAvgData()      
    }
  },
  computed: {
    hasStockSelected() {
      return this.stockId != null
    },
    hasGlassSize() {
      return this.glassSizeId != null
    },
    glassSizeLookup() {
      return this.glassSizeList.map(glassSize => {
        return {
          value: glassSize.id,
          name: `${glassSize.height} x ${glassSize.width}`
        }
      })
    },
    totalAdditionalCosts() {
      return this.additionalCostList.reduce((total, costItem) => {
        return total + parseFloat(costItem.amount || 0)
      }, 0)
    },
    newMovingAveragePrice() {
      const receivedUnits = this.recvItemList.reduce((total, recvItem) => {
        return total + this.calculateItemUnits(recvItem)
      }, 0.0)

      const receivedValue = this.recvItemList.reduce((total, recvItem) => {
        return total + this.calculateItemAdjLinePrice(recvItem)
      }, 0.0)

      const existingUnits = this.unitsOnHand
      const existingValue = this.unitsOnHand * this.movingAverage

      const newTotalUnits = existingUnits + receivedUnits
      const newTotalValue = existingValue + receivedValue

      if(newTotalUnits == 0)
        return 0.0

      const newMovingAvg = newTotalValue / newTotalUnits
      return newMovingAvg
    }
  },
  created() {
    this.InventoryStockTypes = InventoryStockTypes
  },
  methods: {
    requestMovingAvgData() {
      this.$apiService.inventoryService.getGlassSizeMovingAvg(this.stockId, this.glassSizeId, {
        referenceDate: this.referenceDate
      })
      .then(response => {
        const data = response.data.glassSizeInfo

        this.unitsOnHand = data.totalUnitsOnHand
        this.movingAverage = data.movingAverageUnitPrice
      })
      .catch(err => {
        console.error(err)
      })
    },
    addRecvItem() {
      this.recvItemList.push({
        quantity: 0,
        height: 0,
        width: 0,
        unitPrice: 0.0,
      })
    },
    removeRecvItem(index) {
      this.recvItemList.splice(index, 1)
    },
    calculateItemUnits(recvItem) {
      const heightM = parseFloat(recvItem.height) / 1000.0
      const widthM = parseFloat(recvItem.width) / 1000.0
      const quantity = parseInt(recvItem.quantity)
      if(isNaN(heightM) || isNaN(widthM) || isNaN(quantity))
        return 0.0
      return heightM * widthM * quantity
    },
    calculatePrice(recvItem) {
      const units = this.calculateItemUnits(recvItem)
      const unitPrice = parseFloat(recvItem.unitPrice)
      if(isNaN(units) || isNaN(unitPrice))
        return 0.0
      return units * unitPrice
    },
    calculateWeighting(recvItem) {
      const linePrice = this.calculatePrice(recvItem)
      const totalPrice = this.calculateTotalPreAdjPrice()
      if(totalPrice == 0)
        return 0.0
      return (linePrice / totalPrice)
    },
    calculateItemAddCosts(recvItem) {
      const weighting = this.calculateWeighting(recvItem)
      return weighting * this.totalAdditionalCosts
    },
    calculateItemAdjLinePrice(recvItem) {
      const linePrice = this.calculatePrice(recvItem)
      const addCosts = this.calculateItemAddCosts(recvItem)
      return linePrice + addCosts
    },
    calculateTotalPreAdjPrice() {
      return this.recvItemList.reduce((total, item) => {
        return total + this.calculatePrice(item)
      }, 0)
    },
    addAdditionalCost() {
      this.additionalCostList.push({
        description: '',
        amount: 0.0
      })
    },
    removeAdditionalCost(index) {
      this.additionalCostList.splice(index, 1)
    },
  }
}
</script>